import SCRIPT_DO_WRAPPER from "worker:core/do-wrapper";
import SCRIPT_LOCAL_EXPLORER from "worker:local-explorer/explorer";
import {
	kVoid,
	type Service,
	type Worker_Binding,
	type Worker_Module,
} from "../../runtime";
import { CoreBindings } from "../../workers";
import { D1_LOCAL_ENTRY_SERVICE_NAME } from "../../workers/d1/constants";
import { KV_LOCAL_ENTRY_SERVICE_NAME } from "../../workers/kv/constants";
import { R2_LOCAL_ENTRY_SERVICE_NAME } from "../../workers/r2/constants";
import {
	getEnvBindingsOfType,
	getRemoteProxyConnectionString,
	getStorageService,
	WORKER_BINDING_SERVICE_LOOPBACK,
	SERVICE_DEV_REGISTRY_PROXY,
} from "../shared";
import {
	EMAIL_STORE_SERVICE_NAME,
	getUserServiceName,
	LOCAL_EXPLORER_DISK,
	OBSERVABILITY_COLLECTOR_SERVICE_NAME,
	SERVICE_LOCAL_EXPLORER,
} from "./constants";
import type {
	DurableObjectClassNames,
	ParsedInstanceOptions,
	ParsedWorkerOptions,
	WorkflowOption,
} from "../shared";
import type {
	BindingIdMap,
	ExplorerWorkerOpts,
	WorkerResourceBindings,
	WorkflowBindingInfo,
} from "./types";

export interface ExplorerServicesOptions {
	localExplorerUiPath: string;
	proxyBindings: Worker_Binding[];
	bindingIdMap: BindingIdMap;
	hasDurableObjects: boolean;
	workerNames: string[];
	explorerWorkerOpts: ExplorerWorkerOpts;
	telemetry: {
		enabled: boolean;
		deviceId?: string;
	};
	/** Whether local observability is enabled — gates the collector binding. */
	observabilityEnabled: boolean;
	sharedOptions: Pick<
		ParsedInstanceOptions,
		"resourcePersistencePath" | "unsafeEnableSharedStorage"
	>;
}

/**
 * Creates the services needed for the local explorer feature.
 */
export function getExplorerServices(
	options: ExplorerServicesOptions
): Service[] {
	const {
		localExplorerUiPath,
		proxyBindings,
		bindingIdMap,
		hasDurableObjects,
		workerNames,
		explorerWorkerOpts,
		telemetry,
		observabilityEnabled,
		sharedOptions,
	} = options;
	const workflowProxyBindings = proxyBindings.filter((binding) =>
		binding.name?.startsWith(
			`${CoreBindings.DURABLE_OBJECT_NAMESPACE_PROXY}:workflows:`
		)
	);

	const explorerBindings: Worker_Binding[] = [
		// Workflow creation still uses the configured binding API. D1, KV and R2
		// operations use the dedicated internal storage service bindings below.
		...workflowProxyBindings,
		{
			name: CoreBindings.JSON_LOCAL_EXPLORER_BINDING_MAP,
			json: JSON.stringify(bindingIdMap),
		},
		{
			name: CoreBindings.EXPLORER_DISK,
			service: { name: LOCAL_EXPLORER_DISK },
		},
		// Loopback service for accessing Node.js endpoints:
		// - /core/dev-registry for cross-instance aggregation
		// - /core/do-storage for using DO storage to list objects
		WORKER_BINDING_SERVICE_LOOPBACK,
		// Worker names for this instance, used to filter self from registry during aggregation
		{
			name: CoreBindings.JSON_LOCAL_EXPLORER_WORKER_NAMES,
			json: JSON.stringify(workerNames),
		},
		{
			name: CoreBindings.SERVICE_D1,
			service: getStorageService(
				D1_LOCAL_ENTRY_SERVICE_NAME,
				{},
				sharedOptions
			),
		},
		{
			name: CoreBindings.SERVICE_KV,
			service: getStorageService(
				KV_LOCAL_ENTRY_SERVICE_NAME,
				{},
				sharedOptions
			),
		},
		{
			name: CoreBindings.SERVICE_R2,
			service: getStorageService(
				R2_LOCAL_ENTRY_SERVICE_NAME,
				{},
				sharedOptions
			),
		},
		// Per-worker resource bindings for the /local/workers endpoint
		{
			name: CoreBindings.JSON_EXPLORER_WORKER_OPTS,
			json: JSON.stringify(explorerWorkerOpts),
		},
		{
			name: CoreBindings.JSON_TELEMETRY_CONFIG,
			json: JSON.stringify(telemetry),
		},
		{
			name: CoreBindings.DEV_REGISTRY_DEBUG_PORT,
			// workerdDebugPort bindings don't have any additional configuration
			workerdDebugPort: kVoid,
		},
		// The email store service is registered alongside the explorer (see the
		// core plugin's getServices), so it's always available to read from here.
		{
			name: CoreBindings.SERVICE_EMAIL_STORE,
			service: { name: EMAIL_STORE_SERVICE_NAME },
		},
		// Direct service bindings to each user worker in this instance. These let
		// the explorer invoke a worker's handlers (e.g. `email()`.
		...workerNames.map((name) => ({
			name: `${CoreBindings.SERVICE_EXPLORER_USER_WORKER_PREFIX}${name}`,
			service: { name: getUserServiceName(name) },
		})),
	];

	// Only bind the observability collector when observability is enabled —
	// otherwise the service doesn't exist and workerd would fail to start.
	if (observabilityEnabled) {
		explorerBindings.push({
			name: CoreBindings.SERVICE_OBSERVABILITY_COLLECTOR,
			service: {
				name: getUserServiceName(OBSERVABILITY_COLLECTOR_SERVICE_NAME),
			},
		});
	}

	if (hasDurableObjects) {
		// Add Durable Object namespace bindings for the explorer
		// Yes we are binding to 'unbound' DOs, but that has no effect
		// on the user's access via ctx.exports
		for (const durableObject of Object.values(bindingIdMap.do)) {
			explorerBindings.push({
				name: durableObject.binding,
				durableObjectNamespace: {
					className: durableObject.className,
					serviceName: getUserServiceName(durableObject.scriptName),
				},
			});
		}
	}

	// Add Engine DO namespace bindings for each workflow.
	// This gives the explorer direct access to Engine DOs via idFromString()
	// for the instance detail view. Same pattern as DO namespace bindings above.
	// The Engine DO has no alarms and its constructor is idempotent, so waking
	// it up for reads is safe.
	for (const workflowInfo of Object.values(bindingIdMap.workflows)) {
		explorerBindings.push({
			name: workflowInfo.engineBinding,
			durableObjectNamespace: {
				className: "Engine",
				serviceName: `workflows:${workflowInfo.name}`,
			},
		});
	}

	return [
		// Disk service for serving explorer UI assets
		{
			name: LOCAL_EXPLORER_DISK,
			disk: { path: localExplorerUiPath, writable: false },
		},
		{
			name: SERVICE_LOCAL_EXPLORER,
			worker: {
				compatibilityDate: "2026-01-01",
				compatibilityFlags: ["nodejs_compat"],
				modules: [
					{
						name: "explorer.worker.js",
						esModule: SCRIPT_LOCAL_EXPLORER(),
					},
				],
				bindings: explorerBindings,
			},
		},
	];
}

/**
 * Build binding ID map from worker options, proxy bindings, Durable Object
 * class names, and workflow options.
 * Maps resource IDs to binding information for the local explorer.
 */
export function constructExplorerBindingMap(
	allWorkerOpts: ParsedWorkerOptions[],
	proxyBindings: Worker_Binding[],
	durableObjectClassNames: DurableObjectClassNames,
	workflowOptions?: Map<string, WorkflowOption>
): BindingIdMap {
	const IDToBindingName: BindingIdMap = {
		d1: {},
		kv: {},
		do: {},
		r2: {},
		workflows: {},
	};

	for (const workerOpts of allWorkerOpts) {
		for (const [bindingName, binding] of getEnvBindingsOfType(
			workerOpts.config,
			"d1"
		)) {
			if (
				getRemoteProxyConnectionString(binding, workerOpts.dev) === undefined
			) {
				IDToBindingName.d1[binding.id] = bindingName;
			}
		}

		for (const [bindingName, binding] of getEnvBindingsOfType(
			workerOpts.config,
			"kv"
		)) {
			if (
				getRemoteProxyConnectionString(binding, workerOpts.dev) === undefined
			) {
				IDToBindingName.kv[binding.id] = bindingName;
			}
		}

		for (const [bindingName, binding] of getEnvBindingsOfType(
			workerOpts.config,
			"r2"
		)) {
			if (
				getRemoteProxyConnectionString(binding, workerOpts.dev) === undefined
			) {
				IDToBindingName.r2[binding.name] = bindingName;
			}
		}
	}

	// Handle DOs separately, since we need more information than is
	// present in proxy bindings.
	// durableObjectClassNames includes internal and unbound DOs,
	// but not external ones, which we don't want anyway.
	for (const [serviceName, classMap] of durableObjectClassNames) {
		// Skip the dev registry proxy service (hosts external DO proxies)
		if (serviceName === getUserServiceName(SERVICE_DEV_REGISTRY_PROXY)) {
			continue;
		}
		const scriptName = serviceName.replace(/^core:user:/, "");
		for (const [className, classInfo] of classMap) {
			const uniqueKey = `${scriptName}-${className}`;
			IDToBindingName.do[uniqueKey] = {
				className,
				scriptName,
				useSQLite: classInfo.enableSql ?? false,
				binding: `EXPLORER_DO_${uniqueKey}`,
			};
		}
	}

	// Handle Workflows: detect workflow proxy bindings by the "workflows:" prefix
	// and enrich with workflow option metadata.
	// Workflow proxy binding names follow: MINIFLARE_PROXY:workflows:<worker>:<bindingName>
	if (workflowOptions) {
		for (const binding of proxyBindings) {
			if (
				!binding.name?.startsWith(
					`${CoreBindings.DURABLE_OBJECT_NAMESPACE_PROXY}:workflows:`
				)
			) {
				continue;
			}
			if (!("wrapped" in binding)) {
				continue;
			}

			// Extract the workflow name from the inner binding service name ("workflows:<name>")
			const [innerBinding] = binding.wrapped?.innerBindings ?? [];
			if (!innerBinding || !("service" in innerBinding)) {
				continue;
			}
			const serviceName = innerBinding.service?.name;
			if (!serviceName?.startsWith("workflows:")) {
				continue;
			}
			const workflowName = serviceName.replace(/^workflows:/, "");

			// Find the matching workflow option to get className / scriptName
			const workflowOpt = workflowOptions.get(workflowName);
			if (!workflowOpt) {
				continue;
			}

			IDToBindingName.workflows[workflowName] = {
				name: workflowName,
				className: workflowOpt.className,
				scriptName: workflowOpt.scriptName ?? "",
				binding: binding.name,
				engineBinding: `EXPLORER_WORKFLOW_ENGINE_${workflowName}`,
			} satisfies WorkflowBindingInfo;
		}
	}

	return IDToBindingName;
}

/**
 * Build per-worker resource bindings map for the local explorer.
 * Maps worker names to their resource bindings with IDs.
 */
export function constructExplorerWorkerOpts(
	allWorkerOpts: ParsedWorkerOptions[],
	durableObjectClassNames: DurableObjectClassNames
): ExplorerWorkerOpts {
	const result: ExplorerWorkerOpts = {};

	for (const workerOpts of allWorkerOpts) {
		const workerName = workerOpts.config.name;
		if (!workerName) {
			continue;
		}
		const bindings: WorkerResourceBindings = {
			kv: [],
			d1: [],
			r2: [],
			do: [],
			workflows: [],
			sendEmail: [],
		};

		for (const [bindingName, binding] of getEnvBindingsOfType(
			workerOpts.config,
			"kv"
		)) {
			bindings.kv.push({ id: binding.id ?? bindingName, bindingName });
		}

		for (const [bindingName, binding] of getEnvBindingsOfType(
			workerOpts.config,
			"d1"
		)) {
			bindings.d1.push({ id: binding.id ?? bindingName, bindingName });
		}

		for (const [bindingName, binding] of getEnvBindingsOfType(
			workerOpts.config,
			"r2"
		)) {
			bindings.r2.push({ id: binding.name ?? bindingName, bindingName });
		}

		for (const [bindingName, binding] of getEnvBindingsOfType(
			workerOpts.config,
			"durable-object"
		)) {
			const className = binding.exportName;
			const scriptName = binding.worker;
			const serviceName = getUserServiceName(scriptName);
			const uniqueKey = `${scriptName}-${className}`;

			const useSqlite =
				durableObjectClassNames.get(serviceName)?.get(className)?.enableSql ??
				false;

			bindings.do.push({
				id: uniqueKey,
				bindingName,
				className,
				scriptName,
				useSqlite,
			});
		}

		for (const [bindingName, workflow] of getEnvBindingsOfType(
			workerOpts.config,
			"workflow"
		) ?? {}) {
			bindings.workflows.push({
				id: workflow.name,
				bindingName,
				className: workflow.exportName,
				scriptName: workflow.worker ?? workerName,
			});
		}

		for (const [bindingName] of getEnvBindingsOfType(
			workerOpts.config,
			"send-email"
		) ?? {}) {
			bindings.sendEmail.push({ bindingName });
		}

		result[workerName] = bindings;
	}

	return result;
}

/**
 * We need to wrap and extend user Durable Object classes to inject an
 * internal sqlite introspection method for the local explorer to use.
 *
 * This generates a new entry module that replaces the original user entry
 * module, which re-exports everything with DO classes wrapped.
 */
function generateWrapperEntry(
	userEntryName: string,
	durableObjectClassNames: string[]
): string {
	const lines = [
		`import { createDurableObjectWrapper } from "./__mf_do_wrapper.js";`,
		`import * as __mf_original__ from "./${userEntryName}";`,
		// Re-export everything from original module
		`export * from "./${userEntryName}";`,
		`export default __mf_original__.default;`,
	];

	for (const className of durableObjectClassNames) {
		lines.push(
			`export const ${className} = createDurableObjectWrapper(__mf_original__.${className});`
		);
	}

	return lines.join("\n");
}

/**
 * Transforms worker modules to wrap Durable Object classes for the local explorer.
 *
 * This function modifies the modules array to:
 * 1. Insert a new wrapper entry module at the front (workerd uses first module as entry)
 * 2. Inject the wrapper helper module
 * 3. Keep the original entry module with its original name (preserves source mapping)
 */
export function wrapDurableObjectModules(
	modules: Worker_Module[],
	durableObjectClassNames: string[]
): Worker_Module[] {
	const entryModule = modules[0];

	const wrapperEntry = generateWrapperEntry(
		entryModule.name,
		durableObjectClassNames
	);

	// Build new modules array:
	// 1. New wrapper entry module (workerd uses first module as entry)
	// 2. Wrapper helper module with createDurableObjectWrapper
	// 3. All original modules unchanged (entry keeps its name for source mapping)
	return [
		{ name: "__mf_do_wrapper_entry.js", esModule: wrapperEntry },
		{ name: "__mf_do_wrapper.js", esModule: SCRIPT_DO_WRAPPER() },
		...modules,
	];
}
